import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import LocalPostOfficeIcon from "@mui/icons-material/LocalPostOffice";
import { getCartItem, getFromLocalStorage } from "../../config/localStorage";
import { addressInputInterface } from "../../interfaces/forms/addressInput.interface";
import AppContext from "../../config/appContext";
import CardBrandDisplay from "../icons/cardBrandIcons";
import TextField from "@mui/material/TextField";

interface OrderSummaryProps {
  businessInfo: Record<string, string | number | boolean | any>;
  setComponentToDisplay: (selection: string) => void;
  setOrderDetails: (details: Record<string, any>) => void;
  handleSubmitOrder: () => void;
  paymentMethod: Record<string, any>;
  isLoading: boolean;
  orderSubmitError: string;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  businessInfo,
  setComponentToDisplay,
  setOrderDetails,
  handleSubmitOrder,
  paymentMethod,
  isLoading,
  orderSubmitError,
}) => {
  const theme = useTheme();

  const { slug, checkoutSystem, address } = businessInfo;

  const { cartUpdated } = useContext(AppContext);
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [receptionMethod, setReceptionMethod] = useState<string>("");
  const [subtotal, setSubtotal] = useState(0);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [shippingFees, setShippingFees] = useState(0);
  const [taxes, setTaxes] = useState(0);
  const [total, setTotal] = useState(0);
  const [customerInfo, setCustomerInfo] = useState<addressInputInterface>({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
    country: "",
  });
  const [recipientInfo, setRecipientInfo] = useState<addressInputInterface>({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
    country: "",
  });
  const [promoCode, setPromoCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [promoError, setPromoError] = useState("");

  const handleShowPreviousScreen = () => {
    if (checkoutSystem === "PREPAY") {
      setComponentToDisplay("PAYMENTINFORMATION");
    } else {
      if (receptionMethod === "collection" || receptionMethod === "dropOff") {
        setComponentToDisplay("RECIPIENTINFORMATION");
      } else {
        setComponentToDisplay("CUSTOMERINFORMATION");
      }
    }
  };

  useEffect(() => {
    const tempCart = getCartItem(`cart-${slug}`);
    const tempReceptionMethod = tempCart.receptionMethod;
    setReceptionMethod(tempReceptionMethod);
    if (tempCart) {
      setCartItems(tempCart.items);
      const subtotal = tempCart.items.reduce(
        (acc: number, item: any) => acc + item.price * item.qty,
        0
      );
      setSubtotal(subtotal);
      const deliveryFee = tempReceptionMethod === "delivery" ? 5 : 0; // Example delivery fee
      setDeliveryFee(deliveryFee);
      const taxes = parseFloat(
        (subtotal * (businessInfo.paymentInfo.salesTax / 100)).toFixed(2)
      );
      setTaxes(taxes);
      const tempShippingFees =
        tempReceptionMethod === "shipping"
          ? Number(getFromLocalStorage("shippingInfo")?.amount)
          : 0;
      setShippingFees(tempShippingFees);
      setTotal(
        Number((subtotal + deliveryFee + tempShippingFees + taxes).toFixed(2))
      );
    }
  }, [slug, cartUpdated]);

  useEffect(() => {
    const tempCustomerInfo = getFromLocalStorage("customerInfo");
    const tempRecipientInfo = getFromLocalStorage("recipientInfo");
    if (tempCustomerInfo?.firstName) setCustomerInfo(tempCustomerInfo);
    if (tempRecipientInfo?.firstName) setRecipientInfo(tempRecipientInfo);
  }, [cartUpdated]);

  useEffect(() => {
    const tempCart = getCartItem(`cart-${slug}`);

    setOrderDetails({
      cartDetail: tempCart,
      cartItems,
      receptionMethod,
      customerInfo,
      subtotal,
      deliveryFee,
      shippingFees,
      recipientInfo,
      taxes,
      discount,
      total,
    });
  }, [
    cartItems,
    receptionMethod,
    customerInfo,
    subtotal,
    deliveryFee,
    shippingFees,
    recipientInfo,
    taxes,
    total,
    discount,
    slug,
  ]);

  // Utility to calculate discount
  function calculateDiscount(
    amountForDiscount: number,
    discountAmount: number,
    unit: string
  ) {
    if (unit === "percent") {
      return (amountForDiscount * discountAmount) / 100;
    }
    if (unit === "amount") {
      return discountAmount;
    }
    return 0;
  }

  // Load initial discount from cart
  useEffect(() => {
    const tempCart = getCartItem(`cart-${slug}`);
    if (tempCart && tempCart.discountDetails) {
      const { amountForDiscount, shippingDiscount } = tempCart.discountDetails;
      const { discountAmount, unit } = shippingDiscount;
      if (discountAmount) {
        setDiscount(calculateDiscount(amountForDiscount, discountAmount, unit));
      }
    }
  }, [slug, cartUpdated]);

  const handleApplyPromo = () => {
    // TODO: API to check promo code
    // if (promoCode.trim().toLowerCase() === "save10") {
    //   const tempCart = getCartItem(`cart-${slug}`);
    //   const subtotal = tempCart?.items?.reduce(
    //     (acc, item) => acc + item.price * item.qty,
    //     0
    //   );
    //   const promoDiscount = Math.min(10, subtotal * 0.1); // 10% off, max $10
    //   setDiscount(promoDiscount);
    //   setPromoError("");
    // } else {
    //   setPromoError("Invalid promo code");
    //   setDiscount(0);
    // }
  };

  // Update total when discount changes
  useEffect(() => {
    setTotal(
      Number(
        (subtotal + deliveryFee + shippingFees + taxes - discount).toFixed(2)
      )
    );
  }, [subtotal, deliveryFee, shippingFees, taxes, discount]);

  return (
    <Container>
      <Box
        sx={{
          mt: 4,
          p: 3,
          padding: 0,
          marginTop: 0,
          color: theme.palette.primary.main,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <IconButton
            onClick={handleShowPreviousScreen}
            sx={{ color: theme.palette.primary.dark }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            sx={{
              fontSize: 18,
              textAlign: "center",
              flexGrow: 1,
            }}
          >
            Order Summary
          </Typography>
        </Box>
        <Divider />
        {/* Section 1: Cart Items */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Items
          </Typography>
          <List>
            {cartItems.map((item) => (
              <ListItem key={item.optionId}>
                <Grid container spacing={1} color={theme.palette.primary.dark}>
                  <Grid item xs={1}>
                    <ListItemText primary={item.qty} />
                  </Grid>
                  <Grid item xs={8}>
                    <ListItemText
                      primary={`${item.productName} ${item.optionName}`}
                    />
                  </Grid>
                  {/* {item.price > 0 && (
                    <Grid item xs={2}>
                      <ListItemText primary={`${item.price.toFixed(2)}`} />
                    </Grid>
                  )} */}
                  {item.price > 0 && (
                    <Grid item xs={3}>
                      <ListItemText
                        primary={`${(item.price * item.qty).toFixed(2)}`}
                      />
                    </Grid>
                  )}
                </Grid>
              </ListItem>
            ))}
          </List>
        </Box>
        <Divider />
        {/* Section 2: Customer Information */}
        <Box sx={{ mt: 2, letterSpacing: 1 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Information
          </Typography>
          <Grid container spacing={2} color={theme.palette.primary.dark}>
            <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
              <PersonIcon sx={{ mr: 1 }} />
              <Typography variant="body2">
                {customerInfo.firstName} {customerInfo.lastName}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
              <PhoneIcon sx={{ mr: 1 }} />
              <Typography variant="body2">
                {customerInfo.phoneNumber}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
              <EmailIcon sx={{ mr: 1 }} />
              <Typography variant="body2">{customerInfo.email}</Typography>
            </Grid>
            <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
              <HomeIcon sx={{ mr: 1 }} />
              <Typography variant="body2">
                {customerInfo.address1}
                {customerInfo.address2 && `, ${customerInfo.address2}`}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
              <LocationCityIcon sx={{ mr: 1 }} />
              <Typography variant="body2">
                {customerInfo.city},&nbsp;{customerInfo.state},&nbsp;
                {customerInfo.country}
              </Typography>
            </Grid>
            {/* <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
              <PublicIcon sx={{ mr: 1 }} />
              <Typography variant="body2">{customerInfo.country}</Typography>
            </Grid> */}
            <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
              <LocalPostOfficeIcon sx={{ mr: 1 }} />
              <Typography variant="body2">{customerInfo.zipcode}</Typography>
            </Grid>
          </Grid>
        </Box>
        <Divider sx={{ marginTop: 3, marginBottom: 3 }} />
        {(receptionMethod === "collection" ||
          receptionMethod === "dropOff") && (
          <Box sx={{ mt: 2, letterSpacing: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Recipient
            </Typography>
            <Grid container spacing={2} color={theme.palette.primary.dark}>
              <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {recipientInfo.firstName} {recipientInfo.lastName}
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
                <PhoneIcon sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {recipientInfo.phoneNumber}
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
                <EmailIcon sx={{ mr: 1 }} />
                <Typography variant="body2">{recipientInfo.email}</Typography>
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
                <HomeIcon sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {recipientInfo.address1}
                  {recipientInfo.address2 && `, ${recipientInfo.address2}`}
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
                <LocationCityIcon sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {recipientInfo.city}, &nbsp;{recipientInfo.state}, &nbsp;
                  {recipientInfo.country}
                </Typography>
              </Grid>
              {/* <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
                <PublicIco sx={{ mr: 1 }} />
                <Typography variant="body2">{recipientInfo.country}</Typography>
              </Grid> */}
              <Grid item xs={12} sx={{ display: "flex", alignItems: "center" }}>
                <LocalPostOfficeIcon sx={{ mr: 1 }} />
                <Typography variant="body2">{recipientInfo.zipcode}</Typography>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              {/* <Grid item direction={"column"} fontSize={14}>
                <Typography sx={{ mt: 2 }}>Reception Method:</Typography>
              </Grid> */}
              <Grid item>
                <Typography
                  sx={{ mt: 2, fontStyle: "italic" }}
                  color={theme.palette.primary.dark}
                >
                  {/* {receptionMethod.charAt(0).toUpperCase() +
                    receptionMethod.slice(1)} */}
                  You chose for your items to be collected
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid
                item
                direction={"column"}
                fontSize={14}
                fontStyle={"italic"}
                fontWeight={"bold"}
                marginTop={2}
                marginBottom={2}
              >
                Since you selected {receptionMethod}, you can contact the
                service or someone from the service provider is going to contact
                you on the phone number provided to arrange logistic with you!
              </Grid>
            </Grid>
          </Box>
        )}
        {receptionMethod === "pickup" && (
          <Grid container spacing={1}>
            <Grid
              item
              direction={"column"}
              fontSize={14}
              fontStyle={"italic"}
              fontWeight={"bold"}
              marginTop={2}
              marginBottom={2}
            >
              Here is the pickup address: {address?.addressLine1}
              {address?.addressLine2 && `&nbsp;,${address?.addressLine2}`},
              {address?.city}, &nbsp;{address?.state}, &nbsp;{address?.country},
              &nbsp;{address?.zipCode}
            </Grid>
          </Grid>
        )}
        <Divider />

        {/* <Grid container spacing={1}>
              <Grid item direction={"column"} fontSize={14}>
                <Typography sx={{ mt: 2 }}>Reception Method:</Typography>
              </Grid>
              <Grid item>
                <Typography
                  sx={{ mt: 2, fontStyle: "italic" }}
                  color={theme.palette.primary.dark}
                >
                  {receptionMethod.charAt(0).toUpperCase() +
                    receptionMethod.slice(1)}
                </Typography>
              </Grid>
            </Grid> */}

        {/* Section 3: Payment Info */}
        {paymentMethod?.card?.brand && (
          <Box sx={{ mt: 2, paddingBottom: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Payment Info
            </Typography>
            <Grid container spacing={2} textAlign={"center"}>
              <Grid item xs={6}>
                <CardBrandDisplay
                  cardBrand={paymentMethod.card.brand}
                  size={24}
                />
              </Grid>
              <Grid
                item
                container
                xs={6}
                direction={"column"}
                textAlign={"left"}
              >
                <Grid item xs={6} color={theme.palette.primary.dark}>
                  <Typography variant="body2" padding={0}>
                    ****{paymentMethod.card.last4}
                  </Typography>
                </Grid>
                <Grid item xs={6} color={theme.palette.primary.dark}>
                  <Typography variant="body2">
                    Exp {paymentMethod.card.exp_month}/
                    {paymentMethod.card.exp_year}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}
        {/* Section 4: Pricing Details */}

        <Divider />
        <Box sx={{ mt: 2 }}>
          {checkoutSystem === "PREPAY" && (
            <Grid container spacing={2} color={theme.palette.primary.dark}>
              <Grid item xs={6}>
                <Typography>Subtotal:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography align="right">
                  {businessInfo.paymentInfo.currency} &nbsp;
                  {subtotal.toFixed(2)}
                </Typography>
              </Grid>
              {receptionMethod === "delivery" && (
                <>
                  <Grid item xs={6}>
                    <Typography>Delivery Fee:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography align="right">
                      {businessInfo.paymentInfo.currency} &nbsp;
                      {deliveryFee.toFixed(2)}
                    </Typography>
                  </Grid>
                </>
              )}
              {receptionMethod === "shipping" && (
                <>
                  <Grid item xs={6}>
                    <Typography>Shipping Fee:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography align="right">
                      {businessInfo.paymentInfo.currency} &nbsp;
                      {shippingFees.toFixed(2)}
                    </Typography>
                  </Grid>
                </>
              )}
              {taxes > 0 && (
                <>
                  <Grid item xs={6}>
                    <Typography>Taxes:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography align="right">
                      {businessInfo.paymentInfo.currency} &nbsp;
                      {taxes.toFixed(2)}
                    </Typography>
                  </Grid>
                </>
              )}
              {discount > 0 && (
                <>
                  <Grid item xs={6}>
                    <Typography color="success.main">Discount:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography align="right" color="success.main">
                      -{businessInfo.paymentInfo.currency} &nbsp;
                      {discount.toFixed(2)}
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid item xs={6} color={theme.palette.primary.main}>
                <Typography variant="h6">Total:</Typography>
              </Grid>
              <Grid item xs={6} color={theme.palette.primary.main}>
                <Typography variant="h6" align="right">
                  {businessInfo.paymentInfo.currency} &nbsp;{total}
                </Typography>
              </Grid>
            </Grid>
          )}

          {/* Promo Code Input Section */}
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={8}>
                <TextField
                  label="Promo Code"
                  variant="outlined"
                  size="small"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  error={!!promoError}
                  helperText={promoError}
                  fullWidth
                />
              </Grid>
              <Grid item xs={4}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApplyPromo}
                  sx={{ height: "40px" }}
                >
                  Apply
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmitOrder}
              sx={{
                mt: 2,
                bgcolor: theme.palette.secondary.main,
                color: theme.palette.primary.light,
              }}
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Please Wait..." : "Place Order"}
            </Button>
          </Grid>

          {orderSubmitError && (
            <Typography
              variant="body2"
              color="error"
              sx={{ marginTop: 2, textAlign: "center" }}
            >
              {orderSubmitError}
            </Typography>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default OrderSummary;
